import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Detect if the current device is a mobile device
 * Uses user agent and screen size for detection
 */
export function isMobileDevice(): boolean {
  if (typeof window === 'undefined') return false;

  // Check user agent (most reliable for mobile detection)
  const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera;
  const mobileRegex = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini|mobile/i;
  const isMobileUA = mobileRegex.test(userAgent);

  // Check screen size (mobile typically < 1024px for better tablet support)
  const isMobileSize = window.innerWidth < 1024;

  // Check for touch capability
  const hasTouchScreen = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

  // For Chrome DevTools: check if device emulation is active
  // Chrome DevTools mobile simulation typically has small width
  const isChromeDevTools = /Chrome/.test(userAgent) && window.innerWidth < 1024 && window.innerHeight < 1024;

  // Consider it mobile if:
  // 1. User agent matches mobile pattern, OR
  // 2. Small screen with touch capability, OR
  // 3. Chrome DevTools with small screen (for testing)
  if (isMobileUA) return true;
  if (isMobileSize && hasTouchScreen) return true;
  if (isChromeDevTools) return true;
  
  return false;
}

/**
 * Check if the current route is a mobile route (/m/*)
 */
export function isMobileRoute(): boolean {
  if (typeof window === 'undefined') return false;
  return window.location.pathname.startsWith('/m/');
}

/**
 * React hook to detect if device is mobile
 * Updates on window resize
 */
export function useIsMobile(): boolean {
  const [isMobile, setIsMobile] = useState(() => isMobileDevice());

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(isMobileDevice());
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return isMobile;
}

/**
 * React hook to detect if current route is mobile route
 * Uses React Router's useLocation for accurate route detection
 */
export function useIsMobileRoute(): boolean {
  const location = useLocation();
  return location.pathname.startsWith('/m/');
}

/**
 * Detect if device is iOS
 */
export function isIOS(): boolean {
  if (typeof window === 'undefined') return false;
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
}

/**
 * Detect if device is Android
 */
export function isAndroid(): boolean {
  if (typeof window === 'undefined') return false;
  return /android/i.test(navigator.userAgent);
}

